import React, { useReducer, createContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { breakpoint } from '../../styles'

// Exported for creating a mock provider when testing
export const MatchingQueriesContext = createContext()

const defaultMatches = {
  isPhoneOrLarger: false,
  isTabletOrLarger: false,
  isDesktopOrLarger: false,
}

const mediaQueryReducer = (state, action) => {
  switch (action.type) {
    case 'phone':
      return { ...state, isPhoneOrLarger: action.value }
    case 'tablet':
      return { ...state, isTabletOrLarger: action.value }
    case 'desktop':
      return { ...state, isDesktopOrLarger: action.value }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`)
    }
  }
}

const breakpointNames = ['phone', 'tablet', 'desktop']

const MediaQueryProvider = ({ children }) => {
  const [state, dispatch] = useReducer(mediaQueryReducer, defaultMatches)

  useEffect(() => {
    const queriesAndListeners = breakpointNames.map(breakpointName => {
      const mediaQueryList = window.matchMedia(`(min-width: ${breakpoint[breakpointName]}px)`)
      const listener = () => dispatch({ type: breakpointName, value: mediaQueryList.matches })
      listener()

      // The following is to cater for undefined "mediaQueryList.addEventListener" in some browser versions (Safari <= 13)
      // addListener function is used instead allowing core pages to load (example: Sign up page)
      // Reason: Allow a user to sign up and only prompt browser update via intercom at later stages to discourage premature drop off
      if (typeof mediaQueryList.addEventListener === 'function') {
        mediaQueryList.addEventListener('change', listener)
      } else {
        mediaQueryList.addListener(listener)
      }

      return {
        mediaQueryList,
        listener,
      }
    })

    return () =>
      queriesAndListeners.forEach(query => {
        if (typeof query.mediaQueryList.removeEventListener === 'function') {
          query.mediaQueryList.removeEventListener('change', query.listener)
        } else {
          query.mediaQueryList.removeListener(query.listener)
        }
      })
  }, [])

  return <MatchingQueriesContext.Provider value={state}>{children}</MatchingQueriesContext.Provider>
}

export const useMediaQuery = () => {
  const context = React.useContext(MatchingQueriesContext)
  if (context === undefined) {
    throw new Error('useMediaQuery must be used within a MediaQueryProvider')
  }
  return context
}

MediaQueryProvider.propTypes = {
  children: PropTypes.node,
}

export default MediaQueryProvider
